import { useSidebarStore } from '@/stores/sidebar-store';
import { useTranslation } from '@kgc/i18n';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BOTTOM_NAV_HEIGHT,
  DASHBOARD_NAV_ITEM,
  FLAT_NAV_ITEMS,
  NAV_GROUPS,
  type NavItem,
} from './nav-config';

/**
 * NavDrawer — Bottom sheet drawer mobilon (a BottomNav "Több" gombjára)
 * Csoportosított menü + Legutóbbi szekció
 * @see tech-spec-nav-ux-overhaul.md F3-T6
 */
export function NavDrawer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { drawerOpen, closeDrawer, recentItems, trackNavigation } = useSidebarStore();

  // F10 fix: body scroll lock iOS Safari-n
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  const handleNavigate = (href: string, menuItemId?: string) => {
    if (menuItemId) trackNavigation(menuItemId);
    closeDrawer();
    navigate(href);
  };

  // Legutóbbi elemek keresése FLAT_NAV_ITEMS-ből
  const recentNavItems = recentItems
    .map(id => FLAT_NAV_ITEMS.find(item => item.id === id))
    .filter((item): item is (typeof FLAT_NAV_ITEMS)[number] => item !== undefined && !!item.href);

  const renderItem = (item: NavItem) => {
    if (item.href) {
      return (
        <button
          key={item.id}
          onClick={() => handleNavigate(item.href!, item.id)}
          className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-[#1e4a52] dark:text-white/80 transition-colors hover:bg-black/5 dark:hover:bg-white/10"
        >
          <div className="nav-icon-badge" style={{ backgroundColor: item.iconColor ?? '#64748b' }}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d={item.icon} />
            </svg>
          </div>
          <span>{t(item.label)}</span>
        </button>
      );
    }

    return (
      <div key={item.id}>
        <div className="flex items-center gap-3 px-4 py-2">
          <div className="nav-icon-badge" style={{ backgroundColor: item.iconColor ?? '#64748b' }}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d={item.icon} />
            </svg>
          </div>
          <span className="text-sm font-medium text-[#1e4a52] dark:text-white/80">
            {t(item.label)}
          </span>
        </div>
        {item.children && (
          <div className="bg-black/[0.03] dark:bg-black/20">
            {item.children.map(child => (
              <button
                key={child.href}
                onClick={() => handleNavigate(child.href, item.id)}
                className="flex w-full items-center py-2 pl-14 pr-4 text-left text-sm text-[#3d6b73] dark:text-white/80 transition-colors hover:bg-black/[0.03] hover:text-[#1e4a52] dark:hover:text-white"
              >
                {t(child.label)}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Backdrop */}
      {drawerOpen && (
        <div className="fixed inset-0 bg-black/20 z-40" onClick={closeDrawer} aria-hidden="true" />
      )}

      {/* Bottom sheet — F5: aria-hidden when closed; F8: distinct label */}
      <div
        className={`fixed left-0 right-0 max-h-[70vh] bg-white dark:bg-[#1a4f5c] rounded-t-2xl shadow-xl z-50 flex flex-col overflow-hidden ${!drawerOpen ? 'pointer-events-none' : ''}`}
        style={{
          bottom: BOTTOM_NAV_HEIGHT,
          transform: drawerOpen ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 200ms ease-out',
        }}
        role="dialog"
        aria-modal="true"
        aria-hidden={!drawerOpen}
        aria-label={t('nav.moreMenu')}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-2 pb-1 shrink-0">
          <div className="h-1 w-9 rounded-full bg-gray-300 dark:bg-white/20" />
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {/* Legutóbbi szekció */}
          {recentNavItems.length > 0 && (
            <div className="border-b border-gray-100 dark:border-white/10 pb-2">
              <div className="px-4 py-2 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-white/60">
                {t('nav.recentItems')}
              </div>
              {recentNavItems.map(
                item =>
                  item.href && (
                    <button
                      key={item.id}
                      onClick={() => handleNavigate(item.href!, item.id)}
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-[#1e4a52] dark:text-white/80 transition-colors hover:bg-black/5"
                    >
                      {item.iconColor && (
                        <div className="nav-icon-badge" style={{ backgroundColor: item.iconColor }}>
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#fff"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d={item.icon} />
                          </svg>
                        </div>
                      )}
                      <span>{t(item.label)}</span>
                    </button>
                  )
              )}
            </div>
          )}

          {/* Dashboard */}
          {renderItem(DASHBOARD_NAV_ITEM)}

          {/* Csoportosított menü */}
          {NAV_GROUPS.map((group, idx) => (
            <div key={group.groupLabel ?? idx}>
              {group.groupLabel && (
                <div className="mt-3 mb-1 border-t border-gray-100 dark:border-white/10 pt-2 px-4">
                  <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-400 dark:text-white/60">
                    {t(group.groupLabel)}
                  </span>
                </div>
              )}
              {group.items.map(item => renderItem(item))}
            </div>
          ))}

          {/* Bottom padding */}
          <div className="h-4" />
        </div>
      </div>
    </>
  );
}
